import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSchedulerV3Service, serviceName } from '../service';
import { ListBookableAppointmentTypeOptionsIO, ServiceQueries } from '../types';
import {
  UseSchedulerV3InfiniteQueryEndpointArgs,
  useSchedulerV3InfiniteQuery,
} from './use-scheduler-v3-infinite-query';

type ListBookableAppointmentTypeOptionsPageParams = number;
/**
 * A hook that returns an infinite query for the `ListBookableAppointmentTypeOptions` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useListBookableAppointmentTypeOptionsQuery = <
  E = unknown,
  D = ListBookableAppointmentTypeOptionsIO['output']
>({
  options,
  request,
  ...args
}: UseSchedulerV3InfiniteQueryEndpointArgs<
  'ListBookableAppointmentTypeOptions',
  E,
  D,
  ListBookableAppointmentTypeOptionsPageParams
>) =>
  useSchedulerV3InfiniteQuery<'ListBookableAppointmentTypeOptions', E, D, ListBookableAppointmentTypeOptionsPageParams>(
    {
      endpointName: 'ListBookableAppointmentTypeOptions',
      ...args,
      request: ({ pageParam = 1 }) => {
        const requestWithPageParams: ListBookableAppointmentTypeOptionsIO['input'] = {
          ...request,
          page: pageParam,
        };
        return requestWithPageParams;
      },
      options: {
        ...options,
        getNextPageParam: (lastPage, allPages) => {
          const totalPages = Math.ceil((lastPage.totalCount ?? 0) / (request.limit ?? 10));
          return allPages.length < totalPages ? allPages.length + 1 : undefined;
        },
      },
    }
  );

/**
 * A hook that returns a function to fetch the query for the `ListBookableAppointmentTypeOptions` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchListBookableAppointmentTypeOptions = () => {
  const queryClient = useQueryClient();

  const fetchListBookableAppointmentTypeOptions = async <
    E = unknown,
    D = ListBookableAppointmentTypeOptionsIO['output']
  >({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSchedulerV3InfiniteQueryEndpointArgs<'ListBookableAppointmentTypeOptions', E, D>, 'options'> & {
    options: LimitedSchemaFetchQueryOptions<
      ListBookableAppointmentTypeOptionsIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListBookableAppointmentTypeOptions'>>
    >;
  }) => {
    try {
      const response = await queryClient.fetchQuery<
        ListBookableAppointmentTypeOptionsIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'ListBookableAppointmentTypeOptions'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'ListBookableAppointmentTypeOptions'>({
          serviceName,
          endpointName: 'ListBookableAppointmentTypeOptions',
          request,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSchedulerV3Service.ListBookableAppointmentTypeOptions(request, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError ?? ({} as D);
      }
      throw error;
    }
  };

  return fetchListBookableAppointmentTypeOptions;
};
